import { useContext, useEffect, useState } from "react";
import { IoMdArrowRoundBack } from "react-icons/io";
import toast from "react-hot-toast";
import {
  getTaskById,
  postComments,
  updateTaskStatus,
  uploadTaskFile,
  deleteTask,
} from "../../../Services/taskServices";
import { useParams, useNavigate } from "react-router-dom";
import { MdAddComment } from "react-icons/md";
import { HiViewfinderCircle } from "react-icons/hi2";
import { Formik, Form, } from "formik";
import FloatingInput from "../../../Components/Common/FloatingInput";
import { Loader } from "../../../Components/Common";
import { ERROR, REQUIRED, NOT_ASSIGNED, SUCCESS, STATUS, ROLES } from "../constant";
import { TASK_PAGE } from "../../../routes";
import { FiDownload, FiTrash2 } from "react-icons/fi";
import { downloadFile } from "../../Folders/Components/downloadUtils";
import { UserContext } from "../../../Context";
import { extractErrorMessage } from "../../../Utils/extractErrorMessage";

const ViewDetails = () => {
  const [taskData, setTaskData] = useState(null);
  const { taskId } = useParams();
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentData, setCommentData] = useState("");
  const [getComment, setComment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [commentTouched, setCommentTouched] = useState(false);
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const {user} = useContext(UserContext);
  const userRole = user?.role;
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);


  const handleDeleteTask = async () => {
    if (!taskId) return;

    const confirmed = window.confirm(
      REQUIRED.DELETE_CONFIRMATION
    );
    if (!confirmed) return;

    setLoading(true);
    try {
      const response = await deleteTask(taskId);
      if (response.status === 200) {
        toast.success(SUCCESS.DELETION_DONE);
      } else {
        const msg = extractErrorMessage(response) || ERROR.DELETE_NOT_DONE;
        toast.error(msg);
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || ERROR.DELETE_NOT_DONE;
      toast.error(msg);
    } finally {
      navigate(TASK_PAGE);
      setLoading(false);
    }
  };

  const fetchSingleTaskDetails = async (id) => {
    setLoading(true);
    try {
      const response = await getTaskById(id);
      if (response.status === 200) {
        setTaskData(response.data);
        setComment(response.data?.comments);
        if (response.data?.photo) {
          setUploadedFile(response.data.photo);
        }
      } else {
        const msg = extractErrorMessage(response) || ERROR.TASK_DETAILS;
        toast.error(msg);
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || ERROR.TASK_DETAILS;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const onDownloadFile = async (file) => {
    try {
      await downloadFile(file);
      toast.success(SUCCESS.DOWNLOAD_SUCCESSFUL);
    } catch (error) {
      const msg = extractErrorMessage(error) || ERROR.DOWNLOAD_NOT_SUCCESSFUL;
      toast.error(msg);
    }
  };

  //send comments
  const submitComments = async (id, commentData) => {
    setLoading(true);
    try {
      const response = await postComments(id, { comment: commentData });
      if (response.status === 201) {
        setComment((prev) => {
          return [response.data, ...prev];
        });
        toast.success(SUCCESS.COMMENT);
      } else {
        const msg = extractErrorMessage(response) || ERROR.COMMENT;
        toast.error(msg);
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || ERROR.COMMENT;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (taskId) {
      fetchSingleTaskDetails(taskId);
    }
  }, [taskId]);

  const handleSubmitComment = () => {
    setCommentTouched(true);

    if (!commentData.trim()) {
      return;
    }

    submitComments(taskData.id, commentData);
    setShowCommentModal(false);
    setCommentData("");
    setCommentTouched(false);
  };

  const handleStatusChange = (action) => {
    updateTaskStatus(taskId, { action }).then((response) => {
      if (response.status === 200) {
        setTaskData(response.data);
        toast.success(SUCCESS.UPDATE_STATUS);
      } else {
        const msg = extractErrorMessage(response) || ERROR.UPDATE_STATUS;
        toast.error(msg);
      }
    });
  };

  const handleFileUpload = async () => {
    if (!selectedFile || !taskId) return;

    const formData = new FormData();
    formData.append("photo", selectedFile); // backend should expect "file"

    setLoading(true);
    try {
      const response = await uploadTaskFile(taskId, formData); // Define this API below
      if (response.status === 200) {
        toast.success(SUCCESS.UPLOAD_SUCCESSFUL);

        const fileUrl = response.data?.photo;
        if (fileUrl) {
          setUploadedFile(fileUrl);
        }
        setSelectedFile(null);
      } else {
        const msg = extractErrorMessage(response) || ERROR.UPLOAD_UNSUCCESSFUL;
        toast.error(msg);
      }
    } catch (err) {
      const msg = extractErrorMessage(err) || ERROR.UPLOAD_UNSUCCESSFUL;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const initialValues = {
    title: taskData?.title || "",
    description: taskData?.description || "",
    status: taskData?.status
      ? taskData.status
        .toLowerCase()
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
      : "",
    estimated_end_date: taskData?.estimated_end_date || "",
    created: taskData?.created?.split("T")[0] || "",
    assignee: taskData?.assignee_details
      ? `${taskData.assignee_details.first_name} ${taskData.assignee_details.last_name}`
      : NOT_ASSIGNED,
    site: taskData?.site_details?.name || "N/A",
    assigned_by: taskData?.created_by
  };

const getApprovalLabels = () => {
  if (!taskData?.status || !userRole) return { show: false };

  const { status } = taskData;

  if (status === STATUS.CLOSED || status === STATUS.CANCELLED) return { show: false };

  switch (userRole) {
    case ROLES.SITE_STAFF:
      if (status === STATUS.PENDING) {
        return {
          show: true,
          approveLabel: "Mark In Progress",
          rejectLabel: "Reject",
        };
      }
      if (status === STATUS.IN_PROGRESS) {
        return {
          show: true,
          approveLabel: "Mark As Complete",
          rejectLabel: "Mark As Pending",
        };
      }
      break;

    case ROLES.INCHARGE:
      if (status === STATUS.PENDING_L1_APPROVAL) {
        return {
          show: true,
          approveLabel: "Approve",
          rejectLabel: "Reject",
        };
      }
      break;

    case ROLES.PROJECT_INCHARGE:
      if (status === STATUS.PENDING_L2_APPROVAL) {
        return {
          show: true,
          approveLabel: "Approve",
          rejectLabel: "Reject",
        };
      }
      break;

    case ROLES.ADMIN:
    case ROLES.DIRECTOR_MANAGEMENT:
      return {
        show: true,
        approveLabel: "Approve",
        rejectLabel: "Reject",
      };

    default:
      break;
  }

  return { show: false };
};

useEffect(() => {
  return () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
  };
}, [previewUrl]);


  return (
    <div className="px-4 py-4 sm:px-6 md:px-10 lg:px-20 xl:px-32">
      <div className="flex justify-center">
        {loading ? (
          <Loader />
        ) : taskData ? (
          <Formik initialValues={initialValues} enableReinitialize>
            <Form className="bg-white shadow-lg border border-gray-200 rounded-2xl p-6 sm:p-8 w-full max-w-3xl">
              <div className="relative flex items-center justify-center mb-8">
                <button
                  type="button"
                  onClick={() => navigate(TASK_PAGE)}
                  className="absolute left-0 text-primary text-xl hover:text-secondary"
                >
                  <IoMdArrowRoundBack />
                </button>

                {/* Centered Title */}
                <h2 className="text-2xl font-bold text-primary text-center">
                  Task Details
                </h2>

                <button
                  type="button"
                  onClick={handleDeleteTask}
                  className="absolute right-0 text-xl text-red-500 px-3 py-1 rounded hover:bg-red-300 transition"
                >
                  <FiTrash2 />
                </button>
              </div>

              {/* Grid for grouped inputs */}
              <FloatingInput name="title" label="Title" type="text" disabled />
              <FloatingInput
                name="assignee"
                label="Assignee to"
                type="text"
                disabled
              />
              <FloatingInput
                name="assigned_by"
                label="Assignee By"
                type="text"
                disabled
              />
              <FloatingInput
                name="status"
                label="Status"
                type="text"
                disabled
              />
              <div className="flex gap-2">
                <FloatingInput
                  name="created"
                  label="Start Date"
                  type="text"
                  disabled
                />
                <FloatingInput
                  name="estimated_end_date"
                  label="Estimated End Date"
                  type="text"
                  disabled
                />
              </div>
              <FloatingInput name="site" label="Site" type="text" disabled />

              <FloatingInput
                name="description"
                label="Description"
                as="textarea"
                disabled
                css={"mt-2 w-full"}
              />

              <div className="my-4">
                {uploadedFile && (
                  <div className="flex items-center">
                    <p className="text-sm font-medium text-secondary mb-1">
                      Uploaded File:
                    </p>
                    <a
                      href={uploadedFile}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary underline hover:text-green-600 truncate max-w-[200px] px-2"
                    >
                      {uploadedFile.split("/").pop()}
                    </a>
                    <FiDownload
                      className="text-primary cursor-pointer hover:text-green-600"
                      title="Download File"
                      onClick={() => onDownloadFile(uploadedFile)}
                    />
                  </div>
                )}
                <div className="md:flex items-center gap-4">
                  <input
                    type="file"
                    accept="*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setSelectedFile(file);
                        const url = URL.createObjectURL(file);
                        setPreviewUrl(url);
                      }
                    }}
                  />

                  {selectedFile && previewUrl && (
                    <button
                      type="button"
                      className="text-primary underline flex items-center gap-1 mb-1"
                      onClick={() => setShowPreview(true)}
                    >
                      <HiViewfinderCircle /> View
                    </button>
                  )}

                  {showPreview && previewUrl && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                      <div className="bg-white p-4 rounded-lg max-w-[90%] max-h-[90%] overflow-auto relative">
                        <button
                          className="absolute top-2 right-2 text-red-500 text-xl"
                          onClick={() => setShowPreview(false)}
                        >
                          &times;
                        </button>

                        {/* File type preview logic */}
                        {(() => {
                          const fileType = selectedFile?.type || "";
                          if (fileType.startsWith("image/")) {
                            return (
                              <img
                                src={previewUrl}
                                alt="Preview"
                                className="max-w-full max-h-[80vh] object-contain"
                              />
                            );
                          } else if (fileType === "application/pdf") {
                            return (
                              <iframe
                                src={previewUrl}
                                title="PDF Preview"
                                className="w-full h-[80vh]"
                              />
                            );
                          } else if (
                            fileType.startsWith("text/") ||
                            selectedFile?.name?.endsWith(".txt")
                          ) {
                            return (
                              <iframe
                                src={previewUrl}
                                title="Text Preview"
                                className="w-full h-[80vh]"
                              />
                            );
                          } else {
                            return (
                              <div className="text-center text-gray-600">
                                Cannot preview this file type.
                                <a
                                  href={previewUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block mt-2 text-blue-600 underline"
                                >
                                  Open in new tab
                                </a>
                              </div>
                            );
                          }
                        })()}
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleFileUpload}
                    disabled={!selectedFile}
                    className={`px-4 py-2 text-sm rounded text-white bg-primary hover:bg-primary-dark transition ${
                      !selectedFile ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    Upload
                  </button>
                </div>
              </div>
              {(() => {
                const { show, approveLabel, rejectLabel } = getApprovalLabels();
                if (!show) return null;

                return (
                  <div className="w-full flex justify-center gap-2 mt-4">
                    <button
                      type="button"
                      onClick={() => handleStatusChange("approved")}
                      className="px-2 py-2 text-sm rounded text-white bg-primary hover:bg-primary-dark transition"
                    >
                      {approveLabel}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleStatusChange("rejected")}
                      className="px-2 py-2 text-sm rounded text-white bg-red-600 hover:bg-red-700 transition"
                    >
                      {rejectLabel}
                    </button>
                  </div>
                );
              })()}

              <div className="flex justify-between items-center mt-6">
                {/* Assignee Avatar Section */}
                <h3 className="text-lg font-semibold text-primary flex text-center items-center">
                  Comments{" "}
                  <span className="ml-2 bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                    {getComment.length}
                  </span>
                </h3>
                <button
                  type="button"
                  onClick={() => setShowCommentModal(true)}
                  className="bg-primary text-white px-2 py-2 rounded-lg hover:bg-primary-dark transition"
                >
                  {" "}
                  <MdAddComment />
                </button>
              </div>

              {getComment.length !== 0 && (
                <div
                  id="scrollableDiv"
                  className="mt-6 bg-gray-100 text-gray-600 shadow-md border border-gray-200 max-h-[60vh] overflow-y-scroll rounded-xl p-4 sm:p-6 w-full mx-auto space-y-4"
                >
                  {getComment.map((comment, index) => (
                    <div key={index} className="border-b pb-3">
                      <p className="text-sm text-gray-600 break-words whitespace-pre-wrap">
                        {comment.comment}
                      </p>
                      <div className="text-xs text-opacity-80 mt-1 flex justify-between text-gray-600">
                        {comment.created?.split("T")[0] || "Unknown Date"}
                        <p>
                          {comment.created_by?.first_name}{" "}
                          {comment.created_by?.last_name}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Form>
          </Formik>
        ) : (
          <div className="text-gray-500 text-center py-6">
            Loading task details...
          </div>
        )}
      </div>

      {/* Comment Modal */}
      {showCommentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
          <div className="bg-white w-full max-w-md rounded-xl shadow-xl space-y-4 p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800">
              Add Comment
            </h3>

            <textarea
              rows={4}
              value={commentData}
              onChange={(e) => setCommentData(e.target.value)}
              className={`w-full border ${
                commentTouched && !commentData.trim()
                  ? "border-red-500"
                  : "border-gray-300"
              } rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary`}
              placeholder="Enter your comment here..."
            />

            {commentTouched && !commentData.trim() && (
              <p className="text-sm text-red-500 mt-1">
                Comment cannot be empty
              </p>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowCommentModal(false);
                  setCommentData("");
                  setCommentTouched(false);
                }}
                className="px-3 py-1.5 sm:px-4 sm:py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitComment}
                className="px-3 py-1.5 sm:px-4 sm:py-2 rounded bg-primary text-white hover:bg-primary-dark text-sm"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewDetails;
